import { exec } from 'child_process';
import { promisify } from 'util';
import path from 'path';
import { logger } from '../../utils/logger.js';

const execAsync = promisify(exec);

export interface CLIExecuteOptions {
  command: string;
  subcommand: string;
  args?: string[];
  options?: Record<string, any>;
  workspaceId?: string;
}

export interface CLIResponse {
  success: boolean;
  output?: string;
  data?: any;
  exitCode?: number;
  error?: string;
}

export class CLIService {
  private cliPath: string;
  private timeout: number;

  constructor() {
    // 使用当前项目的 workflow CLI
    this.cliPath = 'workflow';
    this.timeout = 30000; // 30 秒超时
  }

  /**
   * 执行 CLI 命令
   */
  async execute(options: CLIExecuteOptions): Promise<CLIResponse> {
    try {
      // 构建命令
      const command = this.buildCommand(options);
      logger.info(`Executing CLI command: ${command}`);

      // 执行命令
      const { stdout, stderr } = await execAsync(command, {
        timeout: this.timeout,
        cwd: process.cwd(),
        env: {
          ...process.env,
          WORKFLOW_NO_COLOR: '1', // 禁用颜色输出
          WORKFLOW_JSON_OUTPUT: '1' // 启用 JSON 输出
        }
      });

      // 解析输出
      let data: any;
      try {
        // 尝试解析 JSON 输出
        data = JSON.parse(stdout);
      } catch {
        // 如果不是 JSON，则作为纯文本返回
        data = stdout;
      }

      return {
        success: true,
        output: stdout,
        data,
        exitCode: 0,
        error: stderr || undefined
      };
    } catch (error: any) {
      logger.error('CLI execution failed:', error);

      // 处理不同类型的错误
      if (error.code === 'ETIMEDOUT') {
        return {
          success: false,
          error: '命令执行超时',
          exitCode: 408
        };
      }

      if (error.code === 'ENOENT') {
        return {
          success: false,
          error: 'Workflow CLI 未安装或不在 PATH 中',
          exitCode: 503
        };
      }

      return {
        success: false,
        output: error.stdout || '',
        error: error.message || '命令执行失败',
        exitCode: error.code || 1
      };
    }
  }

  /**
   * 构建命令行
   */
  private buildCommand(options: CLIExecuteOptions): string {
    const parts = [this.cliPath, options.command];
    
    if (options.subcommand) {
      parts.push(options.subcommand);
    }

    // 添加参数
    if (options.args && options.args.length > 0) {
      parts.push(...options.args.map(arg => this.escapeArg(arg)));
    }

    // 添加选项
    if (options.options) {
      for (const [key, value] of Object.entries(options.options)) {
        if (value === true) {
          parts.push(`--${key}`);
        } else if (value !== false && value !== null && value !== undefined) {
          parts.push(`--${key}`, this.escapeArg(String(value)));
        }
      }
    }

    return parts.join(' ');
  }

  /**
   * 转义命令行参数
   */
  private escapeArg(arg: string): string {
    // 如果参数包含空格或特殊字符，需要加引号
    if (/[\s"'`$\\]/.test(arg)) {
      return `"${arg.replace(/["\\]/g, '\\$&')}"`;
    }
    return arg;
  }

  /**
   * 验证命令是否有效
   */
  async validateCommand(command: string, subcommand: string): Promise<boolean> {
    // 定义允许的命令列表
    const allowedCommands: Record<string, string[]> = {
      task: ['add', 'list', 'update', 'delete', 'move', 'start', 'complete', 'bulk'],
      tags: ['add', 'remove', 'list'],
      stats: [''],
      sync: [''],
      init: [''],
      'parse-prd': [''],
      ui: [''],
      next: [''],
      done: [''],
      now: [''],
      today: ['']
    };

    // 检查命令是否允许
    if (!allowedCommands[command]) {
      return false;
    }

    // 检查子命令是否允许
    const allowedSubcommands = allowedCommands[command];
    if (subcommand && !allowedSubcommands.includes(subcommand)) {
      return false;
    }

    // 对于没有子命令的命令，确保子命令为空
    if (!subcommand && allowedSubcommands.length === 1 && allowedSubcommands[0] === '') {
      return true;
    }

    return true;
  }

  /**
   * 获取 CLI 状态
   */
  async getStatus(): Promise<{ available: boolean; version?: string }> {
    try {
      // 尝试执行 workflow --version
      const { stdout } = await execAsync(`${this.cliPath} --version`, {
        timeout: 5000
      });

      // 解析版本号
      const versionMatch = stdout.match(/(\d+\.\d+\.\d+)/);
      const version = versionMatch ? versionMatch[1] : undefined;

      return {
        available: true,
        version
      };
    } catch (error) {
      logger.error('Failed to get CLI status:', error);
      return {
        available: false
      };
    }
  }

  /**
   * 执行特定的任务命令（提供更友好的接口）
   */
  async taskCommand(
    action: 'add' | 'update' | 'delete' | 'start' | 'complete',
    args: string[],
    options?: Record<string, any>
  ): Promise<CLIResponse> {
    return this.execute({
      command: 'task',
      subcommand: action,
      args,
      options
    });
  }
}